import { useRef, useState, type ReactNode } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Borrador, Enlaces, IdiomaWeb, Imagen, MapaDelSitio, Marca, TipoImagen, TipoSeccion } from '../../api/ops/web';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { avisoBreveDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { EditorOpciones } from './Opciones';
import { conTextos, hayOpciones, moverSeccion, NOMBRE_IDIOMA, sinProtocolo, textosParaEditar, tituloPropio } from './reglas';
import { CATALOGO_PROVISIONAL } from './textos';

/**
 * Los RV Panel de «Tu web» (13.1 y 13.2): Marca, Google y redes (con el mapa del sitio), Portada, Enlaces, una sección y
 * «Añadir una sección». Cada uno edita una copia del borrador y «Guardar» lo manda entero (`updateSiteDraft`).
 */
type Guardar = (b: Borrador) => Promise<boolean>;
type Subir = (archivo: File, tipo: TipoImagen) => Promise<Imagen | null>;

interface Base {
    abierto: boolean;
    borrador: Borrador;
    movil: boolean;
    onCerrar: () => void;
    guardar: Guardar;
}

/** Copia del borrador mientras el panel está abierto; vuelve a la del servidor al abrirlo otra vez. */
function useCopia(abierto: boolean, borrador: Borrador): [Borrador, (b: Borrador) => void] {
    const [copia, setCopia] = useState(borrador);
    const [visto, setVisto] = useState({ abierto, borrador });
    if (visto.abierto !== abierto || visto.borrador !== borrador) {
        setVisto({ abierto, borrador });
        if (abierto) {
            setCopia(borrador);
        }
    }
    return [copia, setCopia];
}

function useGuardado(guardar: Guardar, onCerrar: () => void): [boolean, (b: Borrador) => void] {
    const [ocupado, setOcupado] = useState(false);
    return [
        ocupado,
        (b) => {
            setOcupado(true);
            guardar(b)
                .then((ok) => {
                    if (ok) {
                        onCerrar();
                    }
                })
                .catch(() => undefined)
                .finally(() => { setOcupado(false); });
        },
    ];
}

function Titulo({ children }: { children: ReactNode }) {
    return <h3 className="rv-wb-ceja">{children}</h3>;
}

/** Una imagen del borrador: la que hay y «Subir» (logo, portada, imagen al compartir o de una sección). */
function SubirImagen({ etiqueta, tipo, actual, imagenes, subir, onCambio, bloqueado }: { etiqueta: string; tipo: TipoImagen; actual: string | null | undefined; imagenes: readonly Imagen[]; subir: Subir; onCambio: (id: string | null) => void; bloqueado: boolean }) {
    const entrada = useRef<HTMLInputElement>(null);
    const [ocupado, setOcupado] = useState(false);
    const imagen = imagenes.find((m) => m.id === actual) ?? null;
    return (
        <div className="rv-wb-imagen">
            {imagen?.url != null ? <img src={imagen.url} alt={imagen.alt_text ?? ''} /> : <span className="rv-ca-baldosa"><Icono nombre="image" /></span>}
            <span className="rv-ca-fila__texto">
                <strong>{etiqueta}</strong>
                <span>{actual == null ? 'Sin imagen' : imagen?.status === 'processing' ? 'Preparándola…' : 'PNG, JPG o WebP'}</span>
            </span>
            {!bloqueado && (
                <>
                    <input
                        ref={entrada}
                        type="file"
                        hidden
                        aria-label={etiqueta}
                        accept={tipo === 'logo' ? 'image/png,image/jpeg,image/webp,image/svg+xml' : 'image/png,image/jpeg,image/webp'}
                        onChange={(e) => {
                            const archivo = e.target.files?.[0];
                            e.target.value = '';
                            if (archivo === undefined) {
                                return;
                            }
                            setOcupado(true);
                            subir(archivo, tipo)
                                .then((m) => {
                                    if (m !== null) {
                                        onCambio(m.id);
                                    }
                                })
                                .catch(() => undefined)
                                .finally(() => { setOcupado(false); });
                        }}
                    />
                    {actual != null && (
                        <Boton tipo="fantasma" icono="trash-2" onClick={() => { onCambio(null); }} aria-label={`Quitar ${etiqueta.toLocaleLowerCase('es')}`}>
                            Quitar
                        </Boton>
                    )}
                    <Boton tipo="secundario" icono="upload" ocupado={ocupado} ocupadoTexto="Subiendo…" onClick={() => entrada.current?.click()}>
                        {actual == null ? 'Subir' : 'Cambiar'}
                    </Boton>
                </>
            )}
        </div>
    );
}

const TEMAS: readonly { valor: Marca['theme']; texto: string; icono: 'sun' | 'moon' | 'monitor' }[] = [
    { valor: 'light', texto: 'Claro', icono: 'sun' },
    { valor: 'dark', texto: 'Oscuro', icono: 'moon' },
    { valor: 'auto', texto: 'Automático', icono: 'monitor' },
];
const LETRAS: readonly { valor: NonNullable<Marca['font']>; texto: string }[] = [
    { valor: 'system', texto: 'La del sistema' },
    { valor: 'sans', texto: 'Sin serifa' },
    { valor: 'serif', texto: 'Con serifa' },
    { valor: 'rounded', texto: 'Redondeada' },
];
const COLOR = /^#[0-9A-Fa-f]{6}$/;

export function PanelMarca({ abierto, borrador, imagenes, soloLectura, movil, onCerrar, guardar, subir }: Base & { imagenes: readonly Imagen[]; soloLectura: boolean; subir: Subir }) {
    const [copia, setCopia] = useCopia(abierto, borrador);
    const [ocupado, enviar] = useGuardado(guardar, onCerrar);
    const marca = copia.brand;
    const fijar = (m: Partial<Marca>) => { setCopia({ ...copia, brand: { ...marca, ...m } }); };
    const malo = !COLOR.test(marca.primary_color) || (marca.accent_color !== undefined && !COLOR.test(marca.accent_color));
    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Marca" eyebrow="Tu web" icono="palette" accion={soloLectura ? '' : 'Guardar'} ocupado={ocupado} inactiva={malo} onAccion={() => { enviar(copia); }} cerrar="Cancelar">
            <div className="rv-wb-form">
                <SubirImagen etiqueta="Logo" tipo="logo" actual={marca.logo_media_id} imagenes={imagenes} subir={subir} onCambio={(id) => { fijar({ logo_media_id: id }); }} bloqueado={soloLectura} />
                <Titulo>Colores</Titulo>
                <div className="rv-wb-color">
                    <i style={{ background: COLOR.test(marca.primary_color) ? marca.primary_color : 'transparent' }} aria-hidden="true" />
                    <Campo etiqueta="Color principal" valor={marca.primary_color} onChange={(v) => { fijar({ primary_color: v.trim() }); }} error={COLOR.test(marca.primary_color) ? null : 'Escríbelo así: #DD1420'} bloqueado={soloLectura} max={7} />
                </div>
                <div className="rv-wb-color">
                    <i style={{ background: marca.accent_color !== undefined && COLOR.test(marca.accent_color) ? marca.accent_color : 'transparent' }} aria-hidden="true" />
                    <Campo
                        etiqueta="Color de acento"
                        valor={marca.accent_color ?? ''}
                        onChange={(v) => {
                            const resto: Marca = { theme: marca.theme, primary_color: marca.primary_color, logo_media_id: marca.logo_media_id ?? null, font: marca.font };
                            setCopia({ ...copia, brand: v.trim() === '' ? resto : { ...marca, accent_color: v.trim() } });
                        }}
                        error={marca.accent_color === undefined || COLOR.test(marca.accent_color) ? null : 'Escríbelo así: #DD1420'}
                        ayuda="Botones y lo elegido."
                        bloqueado={soloLectura}
                        max={7}
                    />
                </div>
                <Titulo>Tema</Titulo>
                <Segmentos etiqueta="Tema" items={TEMAS.map((t) => ({ etiqueta: t.texto, icono: t.icono }))} valor={Math.max(0, TEMAS.findIndex((t) => t.valor === marca.theme))} onCambio={(i) => { if (!soloLectura) { fijar({ theme: TEMAS[i]?.valor ?? 'light' }); } }} />
                <Desplegable etiqueta="Letra" valor={marca.font} opciones={LETRAS.map((l) => ({ valor: l.valor, texto: l.texto }))} onChange={(v) => { fijar({ font: v as NonNullable<Marca['font']> }); }} bloqueado={soloLectura} />
            </div>
        </PanelLateral>
    );
}

const TIPOS_NEGOCIO: readonly { valor: string; texto: string }[] = [
    { valor: 'LocalBusiness', texto: 'Negocio local' },
    { valor: 'Restaurant', texto: 'Restaurante' },
    { valor: 'BarOrPub', texto: 'Bar o pub' },
    { valor: 'NightClub', texto: 'Discoteca' },
    { valor: 'HealthAndBeautyBusiness', texto: 'Salud y belleza' },
    { valor: 'SportsActivityLocation', texto: 'Instalación deportiva' },
    { valor: 'LodgingBusiness', texto: 'Alojamiento' },
    { valor: 'EventVenue', texto: 'Sala de eventos' },
];

const ESTADO_MAPA: Record<MapaDelSitio['status'], string> = { fresh: 'Al día', stale: 'Por actualizar', generating: 'Regenerando', disabled: 'Sin generar' };

export function PanelSeo({ abierto, borrador, idioma, mapa, imagenes, soloLectura, movil, onCerrar, guardar, subir, ops, negocio, puedeRegenerar, onMapa }: Base & { idioma: IdiomaWeb; mapa: MapaDelSitio | null; imagenes: readonly Imagen[]; soloLectura: boolean; subir: Subir; ops: Operaciones; negocio: string; puedeRegenerar: boolean; onMapa: (m: MapaDelSitio) => void }) {
    const [copia, setCopia] = useCopia(abierto, borrador);
    const [ocupado, enviar] = useGuardado(guardar, onCerrar);
    const avisar = useAvisoBreve();
    const [regenerando, setRegenerando] = useState(false);
    const textos = textosParaEditar(copia, idioma);
    const seo = textos.seo;
    const fijar = (s: Partial<typeof seo>) => { setCopia(conTextos(copia, { ...textos, seo: { ...seo, ...s } })); };
    const malo = seo.title.trim() === '' || seo.description.trim() === '';
    const desglose = mapa?.url_breakdown;
    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Google y redes" eyebrow={`Tu web · ${NOMBRE_IDIOMA[idioma]}`} icono="search" accion={soloLectura ? '' : 'Guardar'} ocupado={ocupado} inactiva={malo} onAccion={() => { enviar(copia); }} cerrar="Cancelar">
            <div className="rv-wb-form">
                <Campo etiqueta="Título" valor={seo.title} onChange={(v) => { fijar({ title: v }); }} max={70} ayuda={`${String(seo.title.length)} de 70. Lo que sale en Google.`} bloqueado={soloLectura} />
                <Campo etiqueta="Descripción" valor={seo.description} onChange={(v) => { fijar({ description: v }); }} max={160} filas={3} ayuda={`${String(seo.description.length)} de 160.`} bloqueado={soloLectura} />
                <Campo etiqueta="Ciudad" valor={seo.city ?? ''} onChange={(v) => { fijar({ city: v === '' ? null : v }); }} max={80} bloqueado={soloLectura} />
                <Desplegable
                    etiqueta="Tipo de negocio"
                    valor={copia.structured_data?.schema_type ?? 'LocalBusiness'}
                    opciones={TIPOS_NEGOCIO}
                    onChange={(v) => { setCopia({ ...copia, structured_data: { schema_type: v as NonNullable<NonNullable<Borrador['structured_data']>['schema_type']> } }); }}
                    bloqueado={soloLectura}
                />
                <SubirImagen etiqueta="Imagen al compartir" tipo="social" actual={seo.social_image_media_id} imagenes={imagenes} subir={subir} onCambio={(id) => { fijar({ social_image_media_id: id }); }} bloqueado={soloLectura} />
                <div className="rv-wb-google" aria-label="Así sale en Google">
                    <small>{sinProtocolo(mapa?.public_url)}</small>
                    <strong>{seo.title}</strong>
                    <span>{seo.description}</span>
                </div>
                <Titulo>Mapa del sitio</Titulo>
                {mapa === null ? (
                    <p className="rv-ca-nota rv-ca-nota--gris">Sin datos del mapa del sitio.</p>
                ) : (
                    <div className="rv-wb-mapa">
                        <span className="rv-ca-fila__texto">
                            <strong>
                                {ESTADO_MAPA[mapa.status]} {mapa.generated_at != null && <small>{`· ${diaYHora(mapa.generated_at)}`}</small>}
                            </strong>
                            <span>
                                {[
                                    mapa.url_count === undefined ? null : `${String(mapa.url_count)} direcciones`,
                                    desglose?.home === undefined ? null : `portada ${String(desglose.home)}`,
                                    ...(desglose?.detail_pages ?? []).map((d) => `${d.segment} ${String(d.url_count)}`),
                                    ...(desglose?.locales ?? []).map((l) => `${NOMBRE_IDIOMA[l.locale]} ${String(l.url_count)}`),
                                ]
                                    .filter((x) => x !== null)
                                    .join(' · ')}
                            </span>
                        </span>
                        {puedeRegenerar && !soloLectura && (
                            <Boton
                                tipo="secundario"
                                icono="refresh-cw"
                                ocupado={regenerando || mapa.status === 'generating'}
                                ocupadoTexto="Regenerando"
                                onClick={() => {
                                    setRegenerando(true);
                                    ops.regenerateSitemap(negocio)
                                        .then(onMapa)
                                        .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido regenerar.' }); })
                                        .finally(() => { setRegenerando(false); });
                                }}
                            >
                                Regenerar
                            </Boton>
                        )}
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}

export function PanelPortada({ abierto, borrador, idioma, tipos, imagenes, soloLectura, movil, onCerrar, guardar, subir }: Base & { idioma: IdiomaWeb; tipos: readonly TipoSeccion[]; imagenes: readonly Imagen[]; soloLectura: boolean; subir: Subir }) {
    const [copia, setCopia] = useCopia(abierto, borrador);
    const [ocupado, enviar] = useGuardado(guardar, onCerrar);
    const textos = textosParaEditar(copia, idioma);
    const fijarTexto = (t: { tagline?: string; about?: string }) => { setCopia(conTextos(copia, { ...textos, texts: { ...textos.texts, ...t } })); };
    const portada: NonNullable<Borrador['hero']> = copia.hero ?? { show_business_name: true };
    const acciones = copia.sections.filter((s) => s.visible);
    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Portada" eyebrow={`Tu web · ${NOMBRE_IDIOMA[idioma]}`} icono="image" accion={soloLectura ? '' : 'Guardar'} ocupado={ocupado} onAccion={() => { enviar(copia); }} cerrar="Cancelar">
            <div className="rv-wb-form">
                <SubirImagen etiqueta="Imagen de portada" tipo="cover" actual={portada.cover_media_id} imagenes={imagenes} subir={subir} onCambio={(id) => { setCopia({ ...copia, hero: { ...portada, cover_media_id: id } }); }} bloqueado={soloLectura} />
                <Interruptor etiqueta="Enseñar el nombre del negocio" valor={portada.show_business_name} onChange={(v) => { setCopia({ ...copia, hero: { ...portada, show_business_name: v } }); }} bloqueado={soloLectura} />
                <Campo etiqueta="Lema" valor={textos.texts?.tagline ?? ''} onChange={(v) => { fijarTexto({ tagline: v }); }} max={140} ayuda={`${String((textos.texts?.tagline ?? '').length)} de 140.`} bloqueado={soloLectura} />
                <Campo etiqueta="Sobre nosotros" valor={textos.texts?.about ?? ''} onChange={(v) => { fijarTexto({ about: v }); }} max={2000} filas={5} bloqueado={soloLectura} />
                <Desplegable
                    etiqueta="Acción principal"
                    valor={portada.primary_section_type ?? ''}
                    opciones={[{ valor: '', texto: 'La primera sección' }, ...acciones.map((s) => ({ valor: s.section_type, texto: tipos.find((t) => t.code === s.section_type)?.name ?? s.section_type }))]}
                    onChange={(v) => { setCopia({ ...copia, hero: { ...portada, primary_section_type: v === '' ? null : v } }); }}
                    bloqueado={soloLectura}
                />
            </div>
        </PanelLateral>
    );
}

const WHATSAPP: readonly { valor: NonNullable<Enlaces['whatsapp']>; texto: string }[] = [
    { valor: 'none', texto: 'Sin WhatsApp' },
    { valor: 'connected_channel', texto: 'El WhatsApp conectado en Canales' },
    { valor: 'business_phone', texto: 'El teléfono del negocio' },
];

export function PanelEnlaces({ abierto, borrador, soloLectura, movil, onCerrar, guardar }: Base & { soloLectura: boolean }) {
    const [copia, setCopia] = useCopia(abierto, borrador);
    const [ocupado, enviar] = useGuardado(guardar, onCerrar);
    const enlaces: Enlaces = copia.links ?? { whatsapp: 'none', map: false };
    const items = enlaces.items ?? [];
    const fijar = (e: Partial<Enlaces>) => { setCopia({ ...copia, links: { ...enlaces, ...e } }); };
    const malo = items.some((i) => !/^https:\/\//.test(i.url) || i.label.trim() === '');
    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Enlaces" eyebrow="Tu web" icono="link" accion={soloLectura ? '' : 'Guardar'} ocupado={ocupado} inactiva={malo} onAccion={() => { enviar(copia); }} cerrar="Cancelar">
            <div className="rv-wb-form">
                <Desplegable etiqueta="WhatsApp" valor={enlaces.whatsapp} opciones={WHATSAPP} onChange={(v) => { fijar({ whatsapp: v as NonNullable<Enlaces['whatsapp']> }); }} bloqueado={soloLectura} />
                <Interruptor etiqueta="Enseñar el mapa" ayuda="Con la dirección de la sede." valor={enlaces.map} onChange={(v) => { fijar({ map: v }); }} bloqueado={soloLectura} />
                <Campo etiqueta="Instagram" prefijo="@" valor={enlaces.instagram_handle ?? ''} onChange={(v) => { fijar({ instagram_handle: v.replace(/^@/, '') === '' ? null : v.replace(/^@/, '') }); }} bloqueado={soloLectura} />
                <Titulo>Tus enlaces</Titulo>
                {items.map((item, i) => (
                    <div key={String(i)} className="rv-wb-enlace-propio">
                        <Campo etiqueta="Texto" valor={item.label} max={40} onChange={(v) => { fijar({ items: items.map((x, j) => (j === i ? { ...x, label: v } : x)) }); }} bloqueado={soloLectura} />
                        <Campo etiqueta="Dirección" tipo="url" valor={item.url} onChange={(v) => { fijar({ items: items.map((x, j) => (j === i ? { ...x, url: v.trim() } : x)) }); }} error={item.url === '' || /^https:\/\//.test(item.url) ? null : 'Empieza por https://'} bloqueado={soloLectura} />
                        {!soloLectura && (
                            <Boton tipo="fantasma" icono="trash-2" aria-label={`Quitar «${item.label}»`} onClick={() => { fijar({ items: items.filter((_x, j) => j !== i) }); }}>
                                Quitar
                            </Boton>
                        )}
                    </div>
                ))}
                {!soloLectura && items.length < 5 && (
                    <Boton tipo="fantasma" icono="plus" onClick={() => { fijar({ items: [...items, { label: '', url: '' }] }); }}>
                        Añadir un enlace
                    </Boton>
                )}
                <p className="rv-ca-nota rv-ca-nota--gris">Hasta 5, en este orden.</p>
            </div>
        </PanelLateral>
    );
}

export function PanelNuevaSeccion({ abierto, borrador, tipos, movil, onCerrar, guardar }: Base & { tipos: readonly TipoSeccion[] }) {
    const [ocupado, setOcupado] = useState<string | null>(null);
    const libres = tipos.filter((t) => !borrador.sections.some((s) => s.section_type === t.code));
    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Añadir una sección" eyebrow="Tu web" icono="plus">
            <div className="rv-wb-form">
                <p className="rv-wb-provisional">{CATALOGO_PROVISIONAL}</p>
                {libres.length === 0 && <p className="rv-ca-nota rv-ca-nota--gris">Ya tienes todas las secciones del catálogo.</p>}
                <div className="rv-ca-lista">
                    {libres.map((t) => (
                        <div key={t.code} className="rv-ca-fila-caja">
                            <div className="rv-ca-fila">
                                <span className="rv-ca-baldosa">
                                    <Icono nombre="layout-template" />
                                </span>
                                <span className="rv-ca-fila__texto">
                                    <strong>
                                        {t.name}
                                        {!t.entitlement_active && <Chip estado="problem">No contratado</Chip>}
                                        {!t.indexable && <Chip estado="neutral">No sale en buscadores</Chip>}
                                    </strong>
                                    <span>{t.entitlement_active ? (t.description ?? '') : 'Se queda en el borrador: no se publicará hasta que lo contrates.'}</span>
                                </span>
                                <Boton
                                    tipo="secundario"
                                    icono="plus"
                                    ocupado={ocupado === t.code}
                                    onClick={() => {
                                        setOcupado(t.code);
                                        guardar({ ...borrador, sections: [...borrador.sections, { section_type: t.code, module: t.module, visible: true, position: borrador.sections.length, publishable: t.entitlement_active }] })
                                            .then((ok) => {
                                                if (ok) {
                                                    onCerrar();
                                                }
                                            })
                                            .catch(() => undefined)
                                            .finally(() => { setOcupado(null); });
                                    }}
                                >
                                    Añadir
                                </Boton>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </PanelLateral>
    );
}

export function PanelSeccion({ tipoSeccion, borrador, idioma, tipos, publicUrl, imagenes, soloLectura, movil, onCerrar, guardar, subir, onQuitar }: Omit<Base, 'abierto'> & { tipoSeccion: string; idioma: IdiomaWeb; tipos: readonly TipoSeccion[]; publicUrl: string; imagenes: readonly Imagen[]; soloLectura: boolean; subir: Subir; onQuitar: () => void }) {
    const [copia, setCopia] = useCopia(true, borrador);
    const [ocupado, enviar] = useGuardado(guardar, onCerrar);
    const i = copia.sections.findIndex((s) => s.section_type === tipoSeccion);
    const seccion = copia.sections[i];
    const tipo = tipos.find((t) => t.code === tipoSeccion);
    if (seccion === undefined) {
        return null;
    }
    const textos = textosParaEditar(copia, idioma);
    const titulos = (textos.texts?.section_titles ?? {}) as Record<string, string>;
    const titulo = titulos[tipoSeccion] ?? '';
    const fijar = (cambio: Partial<typeof seccion>) => { setCopia({ ...copia, sections: copia.sections.map((s, j) => (j === i ? { ...s, ...cambio } : s)) }); };
    const nombre = tituloPropio(copia, idioma, tipoSeccion) ?? tipo?.name ?? tipoSeccion;
    return (
        <PanelLateral abierto onCerrar={onCerrar} movil={movil} titulo={nombre} eyebrow={(tipo?.module ?? seccion.module).toLocaleUpperCase('es')} subtitulo={tipo?.name} icono="layout-template" accion={soloLectura ? '' : 'Guardar'} ocupado={ocupado} onAccion={() => { enviar(copia); }} cerrar="Cancelar">
            <div className="rv-wb-form">
                <Campo etiqueta="Título de la sección" valor={titulo} max={80} ayuda={`${String(titulo.length)} de 80. Lo verán tus clientes${idioma === copia.default_locale ? '' : ` en ${NOMBRE_IDIOMA[idioma].toLocaleLowerCase('es')}`}.`} onChange={(v) => { setCopia(conTextos(copia, { ...textos, texts: { ...textos.texts, section_titles: { ...titulos, [tipoSeccion]: v } } })); }} bloqueado={soloLectura} />
                <Interruptor etiqueta="Se ve en la web" ayuda={seccion.publishable ? 'Sale en la web al publicar.' : 'No se publicará: su módulo no está contratado. Se queda en el borrador.'} valor={seccion.visible} onChange={(v) => { fijar({ visible: v }); }} bloqueado={soloLectura} />
                {tipo !== undefined && hayOpciones(tipo.options_schema) && (
                    <>
                        <Titulo>{`Opciones de ${tipo.module}`}</Titulo>
                        <EditorOpciones esquema={tipo.options_schema} valor={seccion.options ?? {}} onChange={(o) => { fijar({ options: o as Record<string, never> }); }} bloqueado={soloLectura} />
                    </>
                )}
                {(tipo?.detail_pages ?? []).map((d) => (
                    <p key={d.segment} className="rv-ca-nota">
                        <Icono nombre="file-text" />
                        <span>{`Cada ficha tiene su página: ${sinProtocolo(publicUrl)}/${d.segment}/… Sale en Google y en el mapa del sitio.`}</span>
                    </p>
                ))}
                <SubirImagen etiqueta="Imagen de la sección" tipo="section" actual={seccion.image_media_id} imagenes={imagenes} subir={subir} onCambio={(id) => { fijar({ image_media_id: id }); }} bloqueado={soloLectura} />
                {!soloLectura && (
                    <>
                        <div className="rv-wb-posicion">
                            <span className="rv-ca-fila__texto">
                                <strong>Posición</strong>
                                <span>{`${String(i + 1)} de ${String(copia.sections.length)}`}</span>
                            </span>
                            <Boton tipo="secundario" icono="arrow-up" disabled={i === 0} onClick={() => { setCopia({ ...copia, sections: moverSeccion(copia.sections, i, -1) }); }}>
                                Subir
                            </Boton>
                            <Boton tipo="secundario" icono="arrow-down" disabled={i === copia.sections.length - 1} onClick={() => { setCopia({ ...copia, sections: moverSeccion(copia.sections, i, 1) }); }}>
                                Bajar
                            </Boton>
                        </div>
                        <div>
                            <Boton tipo="secundario" rojo icono="trash-2" onClick={onQuitar}>
                                Quitar de la web
                            </Boton>
                            <p className="rv-ca-nota rv-ca-nota--gris">Solo sale del borrador. Tienes 5 segundos para deshacerlo.</p>
                        </div>
                    </>
                )}
            </div>
        </PanelLateral>
    );
}
